import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';

import { CommandMapper } from '../core/utils/mappers/command.mapper';
import { ParameterTypeMapper } from '../core/utils/mappers/parameter-type.mapper';
import { RouteMapper } from '../core/utils/mappers/route-mapper';
import { ApiResponseModel } from '../models/core/api-response.model';
import { CommandModel } from '../models/core/command.model';
import { RelatedItemModel } from '../models/core/related-item.model';
import { SearchRequestModel } from '../models/core/search-request.model';
import { ContentTypeEnum } from '../models/enums/content-type.enum';
import { HttpMethodEnum } from '../models/enums/http-method.enum';
import { ParameterTypeEnum } from '../models/enums/parameter-type.enum';
import { EnvironmentService } from './environment.service';

@Injectable({
    providedIn: 'root'
})
export class ControllerService {
    /**
     * Current request's response.
     */
    currentResponse?: ApiResponseModel<any>;
    /**
     * Used to let the caller know the request has ended.
     * Note: Used only for request commands.
     */
    readonly requestFinished = new Subject<ApiResponseModel<any>>();

    readonly viewRefreshed = new Subject<void>();

    private readonly http = inject(HttpClient);
    private readonly router = inject(Router);
    private readonly matDialog = inject(MatDialog);
    private readonly environmentService = inject(EnvironmentService);

    execute(relatedItem: RelatedItemModel, data?: any): void {
        const command = CommandMapper.commands.find(c => c.name === relatedItem.commandName);

        if (!!relatedItem.parameterType && !data && !command?.isRequest) {
            if (relatedItem.httpMethod === HttpMethodEnum.Put) {
                const idIndex = relatedItem.uri.lastIndexOf('/');
                const id = relatedItem.uri.substring(idIndex + 1);

                if (command?.showOnModal) {
                    this.openModalForCommand(command);
                    return;
                }

                this.router.navigate([`${RouteMapper.routesByType[relatedItem.parameterType]}/${id}/edit`]).then();

                return;
            }

            if (command?.showOnModal) {
                this.openModalForCommand(command);
                return;
            }

            this.router.navigate([`${RouteMapper.routesByType[relatedItem.parameterType]}/add`]).then();

            return;
        } else if (!!relatedItem.parameterType && !data) {
            data = this.getDataForType(relatedItem.parameterType);
        }

        this.makeRequest(relatedItem, data).subscribe(r => {
            // The following validation was required to handle the reports related actions not being loaded.
            // This should work for all ActionResults.
            if (!r || !r.type || !r.type.startsWith('ActionResult')) {
                this.currentResponse = r;
            }

            if (!!command) {
                if (command.isRequest) {
                    this.requestFinished.next(r);
                    return;
                }

                if (command.showOnModal) {
                    this.openModalForCommand(command);
                    return;
                }
            }

            // TODO: Travis, we need to validate reports and other sub resources. How they'll behave.
            // How will they the handled once returned?

            if (relatedItem.httpMethod === HttpMethodEnum.Delete) {
                this.router.navigate([this.getNavigationRouteAfterDelete()]).then();
            } else {
                this.router.navigate([`${RouteMapper.routesByType[r.type]}/${r.value.id}`]).then();
            }
        });
    }

    executeSubResource(relatedItem: RelatedItemModel): Observable<ApiResponseModel<any>> {
        return this.makeRequest(relatedItem, {});
    }

    refreshView(): void {
        this.viewRefreshed.next();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private makeRequest<T>(relatedItem: RelatedItemModel, data?: any): Observable<any> {
        let request: Observable<any>;
        const url = `${this.environmentService.baseUrl}${relatedItem.uri}`;
        const headers = new HttpHeaders({
            'Content-Type': ContentTypeEnum.ApplicationJson,
            related: 'true'
        });
        const httpOptions: any = { headers, responseType: undefined };
        const bodyString = data ? JSON.stringify(data) : '';

        // TODO: this should come straight from the backend (POST instead of GET).
        // Remove logic once it's done.
        if (relatedItem.uri.toLowerCase().includes('csv')) {
            relatedItem.httpMethod = HttpMethodEnum.Post;
            httpOptions.responseType = 'blob';
        }

        switch (relatedItem.httpMethod) {
            case HttpMethodEnum.Get:
                request = this.http.get(url, httpOptions);
                break;
            case HttpMethodEnum.Post:
                request = this.http.post(url, bodyString, httpOptions);
                break;
            case HttpMethodEnum.Put:
                request = this.http.put(url, bodyString, httpOptions);
                break;
            case HttpMethodEnum.Delete:
                request = this.http.delete(url, httpOptions);
                break;
        }

        return request;
    }

    private getDataForType(type: string): any {
        const pType = ParameterTypeMapper.types[type];

        switch (pType) {
            case ParameterTypeEnum.GridData:
                return new SearchRequestModel(); // TODO: return grid data (TASK #3400)
            default:
                return undefined;
        }
    }

    /**
     * Get the navigation route url used after deletion of one entity item.
     * Returns '<parent-url>' from '<parent-url>/<id>', '<parent-url>/<id>/', '<parent-url>/<id>/edit'.
     * If the current url does not follow that pattern, just return '/' as fallback.
     */
    private getNavigationRouteAfterDelete(): string {
        const url = this.router.url;
        const partToRemove = url.match(/\/\d+\/edit$|\/\d+\/?$/);

        if (!!partToRemove) {
            return url.substring(0, url.length - partToRemove[0].length);
        }

        return '/'; // fallback to root (this is a sign that this method should cover additional cases).
    }

    private openModalForCommand(command: CommandModel): void {
        const dialogRef = this.matDialog.open(command.component, {
            width: command.modalWidth,
            minWidth: command.modalWidth ? undefined : '80%',
            panelClass: 'full-width-dialog'
        });
        if (!!command.button.dialogClosedAction) {
            const action = command.button.dialogClosedAction;
            dialogRef.afterClosed().subscribe((parameters: any) => {
                action(command.button, parameters ?? {});
            });
        }
    }
}
